import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "syngenta-digital-cropwise-react-ui-kit";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { ReactComponent as CoordinatesIcon } from "../../../assets/images/map-tools/coordinates.svg";
import { ReactComponent as CursorIcon } from "../../../assets/images/map-tools/cursor.svg";
import { ReactComponent as EllipseIcon } from "../../../assets/images/map-tools/ellipse.svg";
import { ReactComponent as FieldNameIcon } from "../../../assets/images/map-tools/fieldname.svg";
import { ReactComponent as MapToolIcon } from "../../../assets/images/map-tools/map_tool.svg";
import { ReactComponent as MeasureIcon } from "../../../assets/images/map-tools/measure.svg";
import { ReactComponent as RoadNameIcon } from "../../../assets/images/map-tools/roadname.svg";
import BaseLayerSelector from "./components/BaseLayerSelector";
import CoordinatesTool from "./components/CoordinatesTool";
import FieldNamesTool from "./components/FieldNamesTool";
import MapControlToggle from "./components/MapControlToggle";
import MeasureIllustration from "./components/MeasureIllustration";
import MeasureTool from "./components/MeasureTool";
import RoadNamesTool from "./components/RoadNamesTool";
import ToolPanel from "./ToolPanel";
import { setMapToolCapturingClicks } from "../../utils/mapToolInteraction";
import {
  type LngLatPoint,
  type MapControlsPanelProps,
} from "./MapControlsPanel.types";
import "./MapControlsPanel.less";

/**
 * Mapbox's bottom-right control corner, where the zoom control lives. The
 * trigger is portalled in so it stacks directly above the +/- buttons.
 */
const MAPBOX_CTRL_CORNER = ".mapboxgl-ctrl-bottom-right";
/** The zoom control itself — the dropdown's bottom edge lines up with it. */
const MAPBOX_ZOOM_CTRL = ".mapboxgl-ctrl-bottom-right .mapboxgl-ctrl-group";
/** Horizontal gap between the control column and the dropdown. */
const DROPDOWN_GAP_PX = 8;

interface DropdownAnchor {
  right: number;
  bottom: number;
}

type ControlConfig =
  | {
      type: "button";
      key: string;
      icon: React.ReactNode;
      label: string;
      active: boolean;
      onClick: () => void;
    }
  | {
      type: "toggle";
      key: string;
      icon: React.ReactNode;
      label: string;
      checked: boolean;
      onChange: (checked: boolean) => void;
      testId: string;
    };

interface PanelState {
  isPanelOpen: boolean;
  isMeasureActive: boolean;
  isCoordinatesActive: boolean;
  isRoadNamesEnabled: boolean;
  isFieldNamesEnabled: boolean;
  currentCoordinates: LngLatPoint | null;
}

const INITIAL_STATE: PanelState = {
  isPanelOpen: false,
  isMeasureActive: false,
  isCoordinatesActive: false,
  isRoadNamesEnabled: false,
  isFieldNamesEnabled: true,
  currentCoordinates: null,
};

/** Resolves the Mapbox control corner once the map container exists. */
const useCtrlCornerContainer = (
  map: mapboxgl.Map | null
): HTMLElement | null => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!map) {
      setContainer(null);
      return;
    }

    setContainer(
      map.getContainer().querySelector<HTMLElement>(MAPBOX_CTRL_CORNER)
    );
  }, [map]);

  return container;
};

/**
 * Positions the dropdown by measuring the real controls rather than relying on
 * CSS offsets: the app already overrides the Mapbox corner inset, and the
 * dropdown must line its bottom edge up with the zoom control whatever else
 * ends up in that column.
 */
const useDropdownAnchor = (
  map: mapboxgl.Map | null,
  isOpen: boolean
): DropdownAnchor | null => {
  const [anchor, setAnchor] = useState<DropdownAnchor | null>(null);

  useEffect(() => {
    if (!map || !isOpen) {
      return;
    }

    const measure = (): void => {
      const container = map.getContainer();
      const corner = container.querySelector<HTMLElement>(MAPBOX_CTRL_CORNER);
      if (!corner) {
        return;
      }

      // Fall back to the corner itself before the zoom control has rendered.
      const bottomEdge =
        container.querySelector<HTMLElement>(MAPBOX_ZOOM_CTRL) ?? corner;

      const mapRect = container.getBoundingClientRect();
      const cornerRect = corner.getBoundingClientRect();
      const bottomRect = bottomEdge.getBoundingClientRect();

      setAnchor({
        right: mapRect.right - cornerRect.left + DROPDOWN_GAP_PX,
        bottom: mapRect.bottom - bottomRect.bottom,
      });
    };

    measure();

    map.on("resize", measure);
    window.addEventListener("resize", measure);

    return () => {
      map.off("resize", measure);
      window.removeEventListener("resize", measure);
    };
  }, [map, isOpen]);

  return anchor;
};

const CoordinatesDisplay: React.FC<{ coords: LngLatPoint | null }> = ({
  coords,
}) => (
  <>
    <div className="coordinates-display">
      <span data-testid="coordinates-readout">
        {coords ? `${coords.lng.toFixed(6)} ${coords.lat.toFixed(6)}` : "--"}
      </span>
    </div>
    <div className="coordinates-cursor-icon">
      <EllipseIcon className="coordinates-ellipse" />
      <CursorIcon className="coordinates-cursor" />
    </div>
  </>
);

const MapControlsPanel: React.FC<MapControlsPanelProps> = ({
  mapRef = null,
  currentFieldId,
  neighboringFields,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<PanelState>(INITIAL_STATE);
  const ctrlCorner = useCtrlCornerContainer(mapRef);
  const anchor = useDropdownAnchor(mapRef, state.isPanelOpen);

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const updateState = useCallback((updates: Partial<PanelState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const label = useCallback(
    (key: string) => formatTranslation(t(`newTrial.mapControls.${key}`)),
    [t]
  );

  const handleMeasureClick = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMeasureActive: !prev.isMeasureActive,
      // Both pointer tools own the map cursor, so only one can run at a time.
      isCoordinatesActive: false,
      currentCoordinates: null,
    }));
  }, []);

  const handleCoordinatesClick = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isCoordinatesActive: !prev.isCoordinatesActive,
      isMeasureActive: false,
      currentCoordinates: prev.isCoordinatesActive
        ? null
        : prev.currentCoordinates,
    }));
  }, []);

  const controlConfigs = useMemo(
    (): ControlConfig[] => [
      {
        type: "button",
        key: "measure",
        icon: <MeasureIcon />,
        label: label("measure"),
        active: state.isMeasureActive,
        onClick: handleMeasureClick,
      },
      {
        type: "button",
        key: "coordinates",
        icon: <CoordinatesIcon />,
        label: label("coordinates"),
        active: state.isCoordinatesActive,
        onClick: handleCoordinatesClick,
      },
      {
        type: "toggle",
        key: "road-names",
        icon: <RoadNameIcon />,
        label: label("roadNames"),
        checked: state.isRoadNamesEnabled,
        onChange: (checked) => {
          updateState({ isRoadNamesEnabled: checked });
        },
        testId: "road-names-toggle",
      },
      {
        type: "toggle",
        key: "field-names",
        icon: <FieldNameIcon />,
        label: label("fieldNames"),
        checked: state.isFieldNamesEnabled,
        onChange: (checked) => {
          updateState({ isFieldNamesEnabled: checked });
        },
        testId: "field-names-toggle",
      },
    ],
    [
      label,
      state.isMeasureActive,
      state.isCoordinatesActive,
      state.isRoadNamesEnabled,
      state.isFieldNamesEnabled,
      handleMeasureClick,
      handleCoordinatesClick,
      updateState,
    ]
  );

  /**
   * Measure and Coordinates take over map clicks. Announce that to the rest of
   * the map so a click aimed at a tool cannot also trigger field selection on
   * the first step — Mapbox delivers the same click to both handlers.
   */
  useEffect(() => {
    setMapToolCapturingClicks(
      state.isMeasureActive || state.isCoordinatesActive
    );
    return () => {
      setMapToolCapturingClicks(false);
    };
  }, [state.isMeasureActive, state.isCoordinatesActive]);

  // Clicking away closes the menu, unless a tool is mid-interaction on the map.
  useEffect(() => {
    if (
      !state.isPanelOpen ||
      state.isMeasureActive ||
      state.isCoordinatesActive
    ) {
      return;
    }

    const handleClick = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        updateState({ isPanelOpen: false });
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [
    state.isPanelOpen,
    state.isMeasureActive,
    state.isCoordinatesActive,
    updateState,
  ]);

  const activeTool = useMemo(() => {
    if (state.isMeasureActive) {
      return {
        title: label("measure"),
        icon: <MeasureIcon />,
        instruction: label("measureInstructions"),
        content: <MeasureIllustration />,
        onClose: () => {
          updateState({ isMeasureActive: false });
        },
      };
    }

    if (state.isCoordinatesActive) {
      return {
        title: label("coordinates"),
        icon: <CoordinatesIcon />,
        instruction: label("coordinatesInstructions"),
        content: <CoordinatesDisplay coords={state.currentCoordinates} />,
        onClose: () => {
          updateState({
            isCoordinatesActive: false,
            currentCoordinates: null,
          });
        },
      };
    }

    return null;
  }, [
    label,
    state.isMeasureActive,
    state.isCoordinatesActive,
    state.currentCoordinates,
    updateState,
  ]);

  const renderControl = (config: ControlConfig): JSX.Element => {
    if (config.type === "toggle") {
      return (
        <MapControlToggle
          icon={config.icon}
          label={config.label}
          checked={config.checked}
          onChange={config.onChange}
          data-testid={config.testId}
        />
      );
    }

    return (
      <button
        type="button"
        className={`map-control-button${config.active ? " active" : ""}`}
        onClick={config.onClick}
        aria-label={config.label}
        aria-pressed={config.active}
        data-testid={`map-control-${config.key}`}
      >
        <span className="map-control-icon">{config.icon}</span>
        <span className="map-control-label">{config.label}</span>
      </button>
    );
  };

  const isTriggerActive =
    state.isPanelOpen || state.isMeasureActive || state.isCoordinatesActive;

  const trigger = (
    <div className="map-controls-container mapboxgl-ctrl">
      {/* Suppressed while the menu is open so it cannot cover the panel. */}
      <Tooltip title={state.isPanelOpen ? "" : label("title")} placement="left">
        <button
          ref={triggerRef}
          type="button"
          className={`map-controls-trigger${isTriggerActive ? " active" : ""}`}
          onClick={() => {
            updateState({ isPanelOpen: !state.isPanelOpen });
          }}
          aria-label={label("title")}
          aria-expanded={state.isPanelOpen}
          data-testid="map-controls-trigger"
        >
          <MapToolIcon />
        </button>
      </Tooltip>
    </div>
  );

  const dropdown =
    state.isPanelOpen && anchor ? (
      <div
        ref={panelRef}
        className="map-controls-dropdown"
        data-testid="map-controls-panel"
        style={{ right: anchor.right, bottom: anchor.bottom }}
      >
        {activeTool ? (
          <ToolPanel
            title={activeTool.title}
            icon={activeTool.icon}
            instruction={activeTool.instruction}
            onClose={activeTool.onClose}
          >
            {activeTool.content}
          </ToolPanel>
        ) : (
          <>
            <div className="map-controls-tools">
              {controlConfigs.map((config) => (
                <div className="map-control-item" key={config.key}>
                  {renderControl(config)}
                </div>
              ))}
            </div>
            <div className="map-controls-layers">
              <BaseLayerSelector mapRef={mapRef} />
            </div>
          </>
        )}
      </div>
    ) : null;

  return (
    <>
      {ctrlCorner ? ReactDOM.createPortal(trigger, ctrlCorner) : null}
      {mapRef && dropdown
        ? ReactDOM.createPortal(dropdown, mapRef.getContainer())
        : null}

      <MeasureTool mapRef={mapRef} isActive={state.isMeasureActive} />
      <CoordinatesTool
        mapRef={mapRef}
        isActive={state.isCoordinatesActive}
        onCoordinatesChange={(coords) => {
          updateState({ currentCoordinates: coords });
        }}
      />
      <RoadNamesTool mapRef={mapRef} isEnabled={state.isRoadNamesEnabled} />
      <FieldNamesTool
        mapRef={mapRef}
        isEnabled={state.isFieldNamesEnabled}
        currentFieldId={currentFieldId}
        fields={neighboringFields}
      />
    </>
  );
};

export default MapControlsPanel;
